// src/components/Navbar.jsx
// ─────────────────────────────────────────────────────────────
//  Navbar wired to react-i18next
//  Language change → i18n.changeLanguage() → RTL/LTR auto-applied
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, X, Menu, ChevronDown, Globe, User, LogOut, LayoutDashboard, Truck } from "lucide-react";
import { useTranslation } from "react-i18next";
import ReactCountryFlag from "react-country-flag"


// ── Language metadata (display labels are always native script) ──
const LANGUAGES = [
  { code: "ar", label: "العربية", short: "AR", countryCode: "SA" },
  { code: "fr", label: "Français", short: "FR", countryCode: "FR" },
  { code: "en", label: "English",  short: "EN", countryCode: "GB" },
];

export default function Navbar({ authUser = null, onLogout }) {
  const { t, i18n } = useTranslation();
  

  // Links visible to unauthenticated visitors
  const PUBLIC_LINKS = [
    { labelKey: "nav.home",         label: "Home",         to: "/" },
    { labelKey: "nav.categories",   label: "Categories",   to: "/categories" },
    { labelKey: "nav.products",     label: "Products",     to: "/products" },
    { labelKey: "nav.perfumes",     label: "Perfumes",     to: "/perfumes" },
    { labelKey: "nav.about",        label: "About",        to: "/about" },
    { labelKey: "nav.how_it_works", label: "How It Works", to: "/how-it-works" },
  ];

  // Full navigation for signed-in users
  const AUTH_LINKS = [
    { labelKey: "nav.dashboard", label: "Dashboard", to: "/dashboard" },
    { labelKey: "nav.products",  label: "Products",  to: "/products" },
    { labelKey: "nav.makeup",    label: "Makeup",    to: "/makeup" },
    { labelKey: "nav.kitchen",   label: "Kitchen",   to: "/kitchen" },
    { labelKey: "nav.clothes",   label: "Clothes",   to: "/clothes" },
    { labelKey: "nav.jewellery", label: "Jewellery", to: "/jewellery" },
    { labelKey: "nav.perfumes",  label: "Perfumes",  to: "/perfumes" },
  ];

  const NAV_LINKS = authUser ? AUTH_LINKS : PUBLIC_LINKS;

  const [isScrolled,     setIsScrolled]     = useState(false);
  const [cartPulse,      setCartPulse]      = useState(false);
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [langOpen,       setLangOpen]       = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const [userOpen,       setUserOpen]       = useState(false);

  // Derive active language from i18n (stays in sync across tabs via localStorage)
  const activeLang = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  const location = useLocation();
  const navigate  = useNavigate();
  const langRef   = useRef(null);
  const userRef   = useRef(null);

  // ─── CART COUNT STATE ─────────────────────────────────────────────
  const [cartCount, setCartCount] = useState(() => {
    try {
      const stored = localStorage.getItem("dar_oulmes_cart");
      if (!stored) return 0;
      const cart = JSON.parse(stored);
      return cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
    } catch {
      return 0;
    }
  });

  // Sync cart count when localStorage changes (cross-tab) or custom event fired
  useEffect(() => {
    const updateCount = () => {
      try {
        const stored = localStorage.getItem("dar_oulmes_cart");
        if (!stored) {
          setCartCount(0);
          return;
        }
        const cart = JSON.parse(stored);
        const total = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
        setCartCount(total);
      } catch {
        setCartCount(0);
      }
    };

    window.addEventListener("storage", updateCount);
    window.addEventListener("cart-updated", updateCount);
    window.dispatchEvent(new Event('cart-updated'));
    return () => {
      window.removeEventListener("storage", updateCount);
      window.removeEventListener("cart-updated", updateCount);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock page scroll while the mobile drawer is open so the backdrop
  // doesn't move behind a long menu.
  useEffect(() => {
    if (menuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [menuOpen]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") {
        setLangOpen(false);
        setUserOpen(false);
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside, { passive: true });
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  const handleCartClick = () => {
    setCartPulse(true);
    setTimeout(() => { setCartPulse(false); navigate("/cart"); }, 600);
  };

  const selectLang = (lang) => {
    i18n.changeLanguage(lang.code); // saves to localStorage + fires languageChanged
    setLangOpen(false);
    setMobileLangOpen(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poiret+One&display=swap');
        /* Add flag specific styles */
        .flag-icon {
          font-size: 1.2em;
          line-height: 1;
          border-radius: 2px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        .lang-option .flag-icon {
          margin-right: 6px;
        }
        [dir="rtl"] .lang-option .flag-icon {
          margin-right: 0;
          margin-left: 6px;
        }
        @keyframes navSlideDown {
          from { opacity:0; transform:translateY(-24px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes goldPing {
          0%,100% { transform:scale(1);   opacity:1; }
          50%      { transform:scale(1.5); opacity:0; }
        }
        @keyframes cartBounce {
          0%,100% { transform:scale(1); }
          30%     { transform:scale(1.25) rotate(-8deg); }
          60%     { transform:scale(0.9)  rotate(6deg); }
        }
        @keyframes mobileMenuSlide {
          from { opacity:0; transform:translateY(-10px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes langDropIn {
          from { opacity:0; transform:translateY(-6px) scale(0.97); }
          to   { opacity:1; transform:translateY(0)   scale(1); }
        }

        * { font-family: 'Poiret One', cursive; }

        /* Cart badge (shadcn style) */
        .cart-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 18px;
          height: 18px;
          padding: 0 5px;
          background-color: #c0392b;
          color: white;
          font-family: 'Poiret One', cursive;
          font-size: 10px;
          font-weight: 700;
          line-height: 1;
          border-radius: 999px;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          pointer-events: none;
        }

        /* Subtle pulse ring */
        .cart-pulse-ring {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: transparent;
          border: 1.5px solid #dfcc25;
          transform: translate(-50%, -50%);
          animation: goldPing 2s ease-in-out infinite;
          pointer-events: none;
        }
        
        .nav-link-pill {
          position: relative;
          padding: 6px 14px;
          border-radius: 999px;
          font-family: 'Poiret One', cursive;
          font-size: 15px;
          letter-spacing: 0.08em;
          color: #1f3d3a;
          text-decoration: none;
          transition: color 0.3s ease;
          cursor: pointer;
        }
        .nav-link-pill::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 999px;
          background: linear-gradient(135deg, rgba(223,204,37,0.22), rgba(200,169,138,0.18));
          opacity: 0;
          transition: opacity 0.3s ease, transform 0.3s ease;
          transform: scaleX(0.6);
        }
        .nav-link-pill:hover::before,
        .nav-link-pill.active::before { opacity:1; transform:scaleX(1); }
        .nav-link-pill::after {
          content: '';
          position: absolute;
          bottom: 2px; left: 50%;
          transform: translateX(-50%) scaleX(0);
          width: 60%; height: 2px;
          border-radius: 2px;
          background: linear-gradient(90deg, #dfcc25, #c8a98a);
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1);
        }
        .nav-link-pill:hover::after,
        .nav-link-pill.active::after { transform: translateX(-50%) scaleX(1); }

        .cart-bounce { animation: cartBounce 0.6s ease; }

        /* ── Language Selector ── */
        .lang-selector { position: relative; }
        .lang-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          font-family: 'Poiret One', cursive;
          font-size: 13px;
          letter-spacing: 0.08em;
          padding: 6px 12px;
          border-radius: 999px;
          border: 1.5px solid rgba(31,61,58,0.28);
          background: rgba(255,255,255,0.5);
          color: #1f3d3a;
          cursor: pointer;
          transition: all 0.25s ease;
          white-space: nowrap;
        }
        .lang-btn:hover,
        .lang-btn.open {
          border-color: #dfcc25;
          background: rgba(223,204,37,0.12);
        }
        .lang-chevron { transition: transform 0.25s ease; }
        .lang-chevron.open { transform: rotate(180deg); }
        .lang-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          min-width: 140px;
          background: rgba(255,252,248,0.97);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(223,204,37,0.35);
          border-radius: 14px;
          box-shadow: 0 12px 36px rgba(31,61,58,0.13);
          overflow: hidden;
          z-index: 1000;
          animation: langDropIn 0.2s ease;
        }

        /* RTL: open dropdown to the left instead */
        [dir="rtl"] .lang-dropdown { right: auto; left: 0; }

        .lang-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          font-family: 'Poiret One', cursive;
          font-size: 14px;
          letter-spacing: 0.06em;
          color: #1f3d3a;
          cursor: pointer;
          transition: background 0.18s ease;
          border: none;
          background: transparent;
          width: 100%;
          text-align: start;
        }
        .lang-option:hover { background: rgba(223,204,37,0.14); }
        .lang-option.selected { color: #b8960c; font-weight: bold; }
        .lang-option + .lang-option { border-top: 1px solid rgba(223,204,37,0.15); }
        .lang-short {
          font-size: 11px;
          opacity: 0.5;
          margin-inline-start: auto;
          padding-inline-start: 8px;
        }

        /* ── User Dropdown ── */
        .user-selector { position: relative; }
        .user-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          font-family: 'Poiret One', cursive;
          padding: 6px 8px 6px 6px;
          border-radius: 999px;
          border: 1.5px solid rgba(223,204,37,0.4);
          background: rgba(223,204,37,0.08);
          color: #1f3d3a;
          cursor: pointer;
          transition: all 0.25s ease;
        }
        .user-btn:hover,
        .user-btn.open {
          border-color: #dfcc25;
          background: rgba(223,204,37,0.18);
          box-shadow: 0 4px 14px rgba(223,204,37,0.25);
        }
        .user-avatar {
          width: 30px; height: 30px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1f3d3a, #2d5c57);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .user-dropdown {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: 260px;                   /* fixed width so long emails wrap cleanly */
          max-width: calc(100vw - 24px);  /* never overflow the viewport on small screens */
          background: rgba(255,252,248,0.99);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(223,204,37,0.4);
          border-radius: 14px;
          box-shadow: 0 16px 44px rgba(31,61,58,0.22), 0 2px 8px rgba(31,61,58,0.08);
          overflow: hidden;
          z-index: 1000;                  /* above header & mobile drawer for certainty */
          animation: langDropIn 0.2s ease;
        }
        [dir="rtl"] .user-dropdown { right: auto; left: 0; }

        .user-dropdown-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 16px;
          border-bottom: 1px solid rgba(223,204,37,0.25);
          background: linear-gradient(135deg, rgba(223,204,37,0.08), rgba(200,169,138,0.06));
          font-family: 'Poiret One', cursive;
        }
        .user-dropdown-header .dh-avatar {
          width: 38px; height: 38px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1f3d3a, #2d5c57);
          display: flex; align-items: center; justify-content: center;
          color: #dfcc25;
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.05em;
          flex-shrink: 0;
          border: 2px solid rgba(223,204,37,0.4);
        }
        .user-dropdown-header .dh-body { min-width: 0; flex: 1; }
        .user-dropdown-header .dh-name {
          font-size: 14px;
          color: #1f3d3a;
          font-weight: 600;
          letter-spacing: 0.04em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .user-dropdown-header .dh-email {
          font-size: 12px;
          color: #7a6a3a;
          letter-spacing: 0.03em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-top: 2px;
        }
        .user-dropdown-header .dh-role {
          display: inline-block;
          margin-top: 6px;
          padding: 2px 8px;
          border-radius: 999px;
          font-size: 10px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          background: rgba(223,204,37,0.18);
          color: #7a6a3a;
          border: 1px solid rgba(223,204,37,0.35);
        }
        .user-dropdown-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          font-family: 'Poiret One', cursive;
          font-size: 14px;
          letter-spacing: 0.06em;
          color: #1f3d3a;
          cursor: pointer;
          transition: background 0.18s ease, padding-inline-start 0.2s ease;
          border: none;
          background: transparent;
          width: 100%;
          text-align: start;
          text-decoration: none;
          white-space: nowrap;
        }
        .user-dropdown-item:hover { background: rgba(223,204,37,0.12); padding-inline-start: 20px; }
        .user-dropdown-item + .user-dropdown-item { border-top: 1px solid rgba(223,204,37,0.1); }
        .user-dropdown-item.danger { color: #b04040; }
        .user-dropdown-item.danger:hover { background: rgba(176,64,64,0.07); }

        /* Login / Register buttons */
        .btn-login {
          font-family: 'Poiret One', cursive;
          font-size: 14px;
          letter-spacing: 0.08em;
          padding: 7px 18px;
          border-radius: 999px;
          border: 1.5px solid rgba(31,61,58,0.35);
          background: transparent;
          color: #1f3d3a;
          cursor: pointer;
          transition: all 0.25s ease;
          white-space: nowrap;
        }
        .btn-login:hover {
          border-color: #dfcc25;
          background: rgba(223,204,37,0.1);
        }
        .btn-register {
          font-family: 'Poiret One', cursive;
          font-size: 14px;
          letter-spacing: 0.08em;
          padding: 7px 18px;
          border-radius: 999px;
          border: 1.5px solid #1f3d3a;
          background: linear-gradient(135deg, #1f3d3a, #2d5c57);
          color: #fff;
          cursor: pointer;
          transition: all 0.25s ease;
          white-space: nowrap;
        }
        .btn-register:hover {
          background: linear-gradient(135deg, #dfcc25, #c8a98a);
          border-color: #dfcc25;
          color: #1f3d3a;
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(223,204,37,0.35);
        }

        .hamburger {
          display: none;
          align-items: center; justify-content: center;
          width: 42px; height: 42px;
          border-radius: 50%;
          background: rgba(255,255,255,0.6);
          border: 1px solid rgba(223,204,37,0.35);
          cursor: pointer;
          transition: all 0.25s ease;
        }
        .hamburger:hover {
          background: rgba(223,204,37,0.12);
          border-color: #dfcc25;
        }

        .mobile-menu {
          display: none;
          position: fixed;
          top: 76px; left: 0; right: 0;
          z-index: 49;
          margin: 0 12px;
          border-radius: 20px;
          background: rgba(255,252,248,0.97);
          backdrop-filter: blur(24px) saturate(180%);
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          border: 1px solid rgba(223,204,37,0.3);
          box-shadow: 0 16px 48px rgba(31,61,58,0.18);
          padding: 20px 24px 24px;
          animation: mobileMenuSlide 0.3s ease;
          /* Cap height and scroll internally so long menus never get cut off */
          max-height: calc(100vh - 96px);
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }
        .mobile-menu.open { display: block; padding-bottom: 20px; }
        /* When scrolled, header sits flush at top (no 12px margin) — shrink the
           menu's top offset accordingly so it stays attached to the header. */
        .navbar-scrolled + .mobile-menu,
        .mobile-menu.is-scrolled { top: 68px; }

        .mobile-nav-link {
          display: block;
          font-family: 'Poiret One';
          font-size: 20px;
          letter-spacing: 0.1em;
          font-weight: 500;
          color: #1f3d3a;
          text-decoration: none;
          padding: 12px 0;
          border-bottom: 1px solid rgba(223,204,37,0.2);
          transition: color 0.2s, padding-inline-start 0.2s;
        }
        .mobile-nav-link:hover,
        .mobile-nav-link.active { color: #b8960c; padding-inline-start: 8px; font-weight: bold !important; }

        .mobile-lang-section {
          border-top: 1px solid rgba(223,204,37,0.2);
          padding-top: 14px;
          margin-top: 4px;
        }
        .mobile-lang-toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 0;
          font-family: 'Poiret One', cursive;
          font-size: 16px;
          letter-spacing: 0.08em;
          color: #1f3d3a;
          cursor: pointer;
          background: transparent;
          border: none;
          width: 100%;
        }
        .mobile-lang-options {
          overflow: hidden;
          max-height: 0;
          transition: max-height 0.3s ease;
        }
        .mobile-lang-options.open { max-height: 200px; }
        .mobile-lang-option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          margin: 4px 0;
          border-radius: 10px;
          font-family: 'Poiret One', cursive;
          font-size: 16px;
          color: #1f3d3a;
          cursor: pointer;
          background: transparent;
          border: 1px solid transparent;
          width: 100%;
          transition: all 0.2s ease;
        }
        .mobile-lang-option:hover {
          background: rgba(223,204,37,0.1);
          border-color: rgba(223,204,37,0.3);
        }
        .mobile-lang-option.selected {
          background: rgba(223,204,37,0.12);
          border-color: rgba(223,204,37,0.4);
          color: #b8960c;
          font-weight: bold;
        }
        .mobile-lang-option .check {
          width: 16px; height: 16px;
          border-radius: 50%;
          border: 1.5px solid rgba(31,61,58,0.25);
          display: flex; align-items: center; justify-content: center;
          font-size: 10px;
          flex-shrink: 0;
        }
        .mobile-lang-option.selected .check {
          background: #dfcc25;
          border-color: #dfcc25;
          color: #1f3d3a;
        }

        .mobile-auth-row {
          display: flex;
          gap: 10px;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid rgba(223,204,37,0.2);
        }
        .mobile-auth-row .btn-login,
        .mobile-auth-row .btn-register {
          flex: 1;
          text-align: center;
          font-size: 15px;
          padding: 10px 0;
        }

        @media (max-width: 768px) {
          .desktop-nav  { display: none !important; }
          .desktop-auth { display: none !important; }
          .hamburger    { display: flex !important; }
        }

        /* Very small phones: tighten spacing so logo + cart + hamburger all fit */
        @media (max-width: 380px) {
          .mobile-menu { margin: 0 8px; padding: 16px 18px 20px; }
          .mobile-nav-link { font-size: 18px; padding: 10px 0; }
          header.nav-header { padding: 0 14px !important; }
          header.nav-header > a > img { width: 44px !important; height: 44px !important; }
          .mobile-controls { gap: 8px !important; }
        }
      `}</style>

      {/* ── NAVBAR BAR ── */}
      <div
        className="w-full flex justify-center fixed top-0 left-0 z-50"
        style={{ animation: "navSlideDown 0.7s cubic-bezier(0.34,1.56,0.64,1) both", height: "80px" }}
      >
        <header
          className="nav-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 20px",
            width: "100%",
            maxWidth: isScrolled ? "100%" : "1280px",
            borderRadius: isScrolled ? "0" : "999px",
            marginTop: isScrolled ? "0" : "12px",
            height: "64px",
            background: isScrolled ? "rgba(255,252,248,0.92)" : "rgba(255,252,248,0.60)",
            backdropFilter: "blur(24px) saturate(180%)",
            WebkitBackdropFilter: "blur(24px) saturate(180%)",
            border: "1px solid rgba(223,204,37,0.25)",
            borderTop: isScrolled ? "2px solid rgba(223,204,37,0.5)" : "1px solid rgba(223,204,37,0.25)",
            boxShadow: isScrolled
              ? "0 8px 40px rgba(31,61,58,0.12), 0 1px 0 rgba(223,204,37,0.2)"
              : "0 12px 48px rgba(31,61,58,0.10), inset 0 1px 0 rgba(255,255,255,0.6)",
            transition: "all 0.5s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {/* ── LOGO ── */}
          <Link to="/" style={{ flexShrink: 0 }}>
            <img
              src="logo 2.png"
              alt="Dar Oulmes"
              style={{
                width: "52px", height: "52px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid rgba(223,204,37,0.4)",
                boxShadow: "0 4px 16px rgba(31,61,58,0.15)",
                transition: "transform 0.4s ease, box-shadow 0.4s ease",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "rotate(-4deg) scale(1.08)";
                e.currentTarget.style.boxShadow = "0 6px 24px rgba(223,204,37,0.5)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "rotate(0deg) scale(1)";
                e.currentTarget.style.boxShadow = "0 4px 16px rgba(31,61,58,0.15)";
              }}
            />
          </Link>

          {/* ── DESKTOP NAV LINKS ── */}
          <nav className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {NAV_LINKS.map(({ labelKey, label, to }) => (
              <Link
                key={to}
                to={to}
                className={`nav-link-pill${location.pathname === to ? " active" : ""}`}
              >
                {t(labelKey, label)}
              </Link>
            ))}
          </nav>

          {/* ── DESKTOP RIGHT SIDE ── */}
          <div className="desktop-auth" style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>

            {/* Language Dropdown */}
            <div className="lang-selector" ref={langRef}>
              <button
                className={`lang-btn${langOpen ? " open" : ""}`}
                onClick={() => setLangOpen(o => !o)}
                aria-label={t("language.label")}
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>{activeLang.flag}</span>
                {activeLang.short}
                <ChevronDown
                  size={12}
                  color="#1f3d3a"
                  className={`lang-chevron${langOpen ? " open" : ""}`}
                />
              </button>

              {langOpen && (
                <div className="lang-dropdown" role="listbox" aria-label={t("language.label")}>
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      role="option"
                      aria-selected={activeLang.code === lang.code}
                      className={`lang-option${activeLang.code === lang.code ? " selected" : ""}`}
                      onClick={() => selectLang(lang)}
                    >
                      <ReactCountryFlag
                        countryCode={lang.countryCode}
                        svg
                        style={{ width: '1.2em', height: '1.2em', borderRadius: 2, marginRight: 6 }}
                        title={lang.label}
                      />
                      {lang.label}
                      <span className="lang-short">{lang.short}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cart (only for authenticated users) */}
            {authUser && (
              <button
                onClick={handleCartClick}
                className={cartPulse ? "cart-bounce" : ""}
                aria-label={t("nav.cart")}
                style={{
                  position: "relative",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  width: "42px", height: "42px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.6)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(223,204,37,0.35)",
                  boxShadow: "0 4px 16px rgba(31,61,58,0.1), inset 0 1px 0 rgba(255,255,255,0.8)",
                  cursor: "pointer",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = "scale(1.1)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(223,204,37,0.4)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 4px 16px rgba(31,61,58,0.1)";
                }}
              >
                <ShoppingCart size={18} color="#1f3d3a" />
                {cartCount > 0 && (
                  <span className="cart-badge">{cartCount > 99 ? '99+' : cartCount}</span>
                )}
                {/* <span className="cart-pulse-ring" /> */}
              </button>
            )}

            {authUser ? (
              <div className="user-selector" ref={userRef}>
                <button
                  className={`user-btn${userOpen ? " open" : ""}`}
                  onClick={() => setUserOpen(o => !o)}
                  aria-label="User menu"
                >
                  <span className="user-avatar">
                    <User size={14} color="#dfcc25" />
                  </span>
                  <ChevronDown
                    size={12}
                    color="#1f3d3a"
                    className={`lang-chevron${userOpen ? " open" : ""}`}
                  />
                </button>

                {userOpen && (
                  <div className="user-dropdown">
                    <div className="user-dropdown-header">
                      <span className="dh-avatar">
                        {(authUser.name || authUser.email || "U").charAt(0).toUpperCase()}
                      </span>
                      <div className="dh-body">
                        <div className="dh-name" title={authUser.name || authUser.email}>
                          {authUser.name || authUser.email}
                        </div>
                        {authUser.name && authUser.email && (
                          <div className="dh-email" title={authUser.email}>
                            {authUser.email}
                          </div>
                        )}
                        {authUser.role && authUser.role !== "USER" && (
                          <div className="dh-role">{authUser.role}</div>
                        )}
                      </div>
                    </div>
                    <Link
                      to="/profile"
                      className="user-dropdown-item"
                      onClick={() => setUserOpen(false)}
                    >
                      <User size={15} color="#1f3d3a" />
                      {t("nav.profile") || "Profile"}
                    </Link>
                    {authUser.role === "ADMIN" && (
                      <Link
                        to="/admin"
                        className="user-dropdown-item"
                        onClick={() => setUserOpen(false)}
                        style={{ color: "#b8960c" }}
                      >
                        <LayoutDashboard size={15} color="#b8960c" />
                        {t("nav.admin_panel") || "Admin Panel"}
                      </Link>
                    )}
                    {authUser.role === "DELIVERY" && (
                      <Link
                        to="/delivery"
                        className="user-dropdown-item"
                        onClick={() => setUserOpen(false)}
                        style={{ color: "#2d7a6a" }}
                      >
                        <Truck size={15} color="#2d7a6a" />
                        {t("nav.delivery_panel") || "Delivery Panel"}
                      </Link>
                    )}
                    <button
                      className="user-dropdown-item danger"
                      onClick={() => { setUserOpen(false); onLogout && onLogout(); }}
                    >
                      <LogOut size={15} color="#b04040" />
                      {t("auth.logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login">
                  <button className="btn-login">{t("auth.login")}</button>
                </Link>
                <Link to="/register">
                  <button className="btn-register">{t("auth.register")}</button>
                </Link>
              </>
            )}
          </div>

          {/* ── MOBILE: Cart + Hamburger ── */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }} className="mobile-controls">
            {authUser && (
              <button
                onClick={handleCartClick}
                className={cartPulse ? "cart-bounce" : ""}
                aria-label={t("nav.cart")}
                style={{
                  position: "relative",
                  display: "none",
                  alignItems: "center", justifyContent: "center",
                  width: "42px", height: "42px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.6)",
                  border: "1px solid rgba(223,204,37,0.35)",
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                }}
                id="mobile-cart-btn"
              >
                <ShoppingCart size={18} color="#1f3d3a" />
                {cartCount > 0 && (
                  <span className="cart-badge">{cartCount > 99 ? '99+' : cartCount}</span>
                )}
                {/* <span className="cart-pulse-ring" /> */}
              </button>
            )}

            <button
              className="hamburger"
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={20} color="#1f3d3a" /> : <Menu size={20} color="#1f3d3a" />}
            </button>
          </div>
        </header>
      </div>

      {/* ── MOBILE DRAWER ── */}
      <div className={`mobile-menu${menuOpen ? " open" : ""}${isScrolled ? " is-scrolled" : ""}`}>
        {NAV_LINKS.map(({ labelKey, label, to }) => (
          <Link
            key={to}
            to={to}
            className={`mobile-nav-link${location.pathname === to ? " active" : ""}`}
          >
            {t(labelKey, label)}
          </Link>
        ))}

        {authUser && (
          <Link
            to="/cart"
            className="mobile-nav-link"
            onClick={() => setMenuOpen(false)}
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <ShoppingCart size={18} />
            {t("nav.cart")}
          </Link>
        )}

        {/* Mobile Language Selector */}
        <div className="mobile-lang-section">
        <button
            className="mobile-lang-toggle"
            onClick={() => setMobileLangOpen(o => !o)}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <ReactCountryFlag
                countryCode={activeLang.countryCode}
                svg
                style={{ width: '1.5em', height: '1.5em', borderRadius: 2 }}
                title={activeLang.label}
              />
              {t("language.label")} —{" "}
              <strong style={{ color: "#b8960c" }}>{activeLang.label}</strong>
            </span>
            <ChevronDown
              size={16}
              color="#1f3d3a"
              style={{
                transition: "transform 0.25s ease",
                transform: mobileLangOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </button>

          <div className={`mobile-lang-options${mobileLangOpen ? " open" : ""}`}>
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                className={`mobile-lang-option${activeLang.code === lang.code ? " selected" : ""}`}
                onClick={() => selectLang(lang)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <ReactCountryFlag
                    countryCode={lang.countryCode}
                    svg
                    style={{ width: '1.2em', height: '1.2em', borderRadius: 2 }}
                    title={lang.label}
                  />
                  {lang.label}
                </span>
                <span className="check">{activeLang.code === lang.code ? "✓" : ""}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mobile-auth-row">
          {authUser ? (
            <>
              <div style={{ width: "100%", borderRadius: 12, border: "1px solid rgba(223,204,37,0.3)", overflow: "hidden" }}>
                <div style={{
                  padding: "10px 14px",
                  fontFamily: "'Poiret One', cursive",
                  fontSize: 13, color: "#7a6a3a",
                  borderBottom: "1px solid rgba(223,204,37,0.2)",
                  background: "rgba(223,204,37,0.06)",
                }}>
                  {authUser.name || authUser.email}
                </div>
                <Link
                  to="/profile"
                  className="user-dropdown-item"
                  onClick={() => setMenuOpen(false)}
                >
                  <User size={15} color="#1f3d3a" />
                  {t("nav.profile") || "Profile"}
                </Link>
                {authUser.role === "ADMIN" && (
                  <Link
                    to="/admin"
                    className="user-dropdown-item"
                    onClick={() => setMenuOpen(false)}
                    style={{ color: "#b8960c" }}
                  >
                    <LayoutDashboard size={15} color="#b8960c" />
                    {t("nav.admin_panel") || "Admin Panel"}
                  </Link>
                )}
                {authUser.role === "DELIVERY" && (
                  <Link
                    to="/delivery"
                    className="user-dropdown-item"
                    onClick={() => setMenuOpen(false)}
                    style={{ color: "#2d7a6a" }}
                  >
                    <Truck size={15} color="#2d7a6a" />
                    {t("nav.delivery_panel") || "Delivery Panel"}
                  </Link>
                )}
                <button
                  className="user-dropdown-item danger"
                  onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
                >
                  <LogOut size={15} color="#b04040" />
                  {t("auth.logout")}
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" style={{ flex: 1 }} onClick={() => setMenuOpen(false)}>
                <button className="btn-login" style={{ width: "100%" }}>{t("auth.login")}</button>
              </Link>
              <Link to="/register" style={{ flex: 1 }} onClick={() => setMenuOpen(false)}>
                <button className="btn-register" style={{ width: "100%" }}>{t("auth.register")}</button>
              </Link>
            </>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          #mobile-cart-btn { display: flex !important; }
        }
        @media (min-width: 769px) {
          .mobile-controls { display: none !important; }
        }
      `}</style>
    </>
  );
}